import React, { useState } from 'react';
import { LockIcon, EyeIcon, EyeOffIcon, ShieldCheckIcon } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { PageHero } from '../layout/PageHero';
/**
 * Client-side passcode gate.
 *
 * NOTE: This is a lightweight access barrier suitable for keeping casual
 * visitors out. Because the passcode is validated in the browser, it is not
 * true server-backed security — treat it as a deterrent, not as protection for
 * highly sensitive data. Access is NOT remembered: the passcode must be
 * re-entered every time the visitor lands on the gated page.
 */
// The authorised passcode. Update this value to change the access code.
const ACCESS_PASSCODE = 'AUC-2026-TRACON';
interface PasscodeGateProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}
export function PasscodeGate({
  children,
  title = 'Restricted Access',
  description = 'This section is available to authorised visitors only. Please enter your access passcode to continue.'
}: PasscodeGateProps) {
  const [unlocked, setUnlocked] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() === ACCESS_PASSCODE) {
      setError(false);
      setUnlocked(true);
    } else {
      setError(true);
    }
  };
  if (unlocked) {
    return <>{children}</>;
  }
  return <div data-id="element-404" data-mp="2tvuk1exg1tk3">
      <PageHero title="Bids & Tenders" subtitle="Authorised access required" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Bids & Tenders'
    }]} data-id="element-405" data-mp="2tvkuttk83qfg" />
      <div className="max-w-md mx-auto px-4 py-16" data-id="element-406" data-mp="38slkzsmrndbt">
        <Card className="text-center" data-id="element-407" data-mp="38r0tycznucip">
          <div className="w-16 h-16 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-408" data-mp="3djbtvfuehyb9">
            <LockIcon size={32} className="text-au-green" data-id="element-409" data-mp="3nfu3zfmbgmyc" />
          </div>
          <h2 className="text-2xl font-serif text-charcoal mb-2" data-id="element-410" data-mp="3ncy5nq6k3w9d">{title}</h2>
          <div className="w-12 h-1 bg-au-gold mx-auto mb-4" data-id="element-411" data-mp="3igz9hgezl9ye" />
          <p className="text-gray-700 leading-relaxed mb-8" data-id="element-412" data-mp="3dgbj8wyl0x58">{description}</p>

          <form onSubmit={handleSubmit} className="text-left" data-id="element-413" data-mp="38ia1cfcw73vc">
            <label htmlFor="passcode" className="block text-sm font-medium text-charcoal mb-2" data-id="element-414" data-mp="38k4ev8lu3tpe">
              Access Passcode
            </label>
            <div className="relative" data-id="element-415" data-mp="2zoexrbrf5lht">
              <input id="passcode" type={showCode ? 'text' : 'password'} value={value} onChange={e => {
              setValue(e.target.value);
              if (error) setError(false);
            }} autoFocus autoComplete="off" aria-invalid={error} aria-describedby={error ? 'passcode-error' : undefined} placeholder="Enter your passcode" className={`w-full px-4 py-3 pr-11 rounded-lg border ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-border focus:ring-au-green'} focus:outline-none focus:ring-2 focus:border-transparent`} data-id="element-416" data-mp="2uqdfuu5qbs7x" />
              <button type="button" onClick={() => setShowCode(s => !s)} aria-label={showCode ? 'Hide passcode' : 'Show passcode'} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-au-green transition-colors" data-id="element-417" data-mp="1a7jctfb0x3pk">
                {showCode ? <EyeOffIcon size={18} data-id="element-418" data-mp="mhp336m03v9j" /> : <EyeIcon size={18} data-id="element-419" data-mp="r9q94askspr4" />}
              </button>
            </div>
            {error && <p id="passcode-error" className="mt-2 text-sm text-red-600" role="alert" data-id="element-420" data-mp="3pzqflt2exu61">
                Incorrect passcode. Please check with the procurement office and
                try again.
              </p>}
            <Button type="submit" className="w-full mt-6" data-id="element-421" data-mp="20g8sydlt5r3h">
              Unlock Access
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-422" data-mp="2a8y04wabsit0">
            <ShieldCheckIcon size={14} data-id="element-423" data-mp="25h66gcvvhwyn" />
            <span data-id="element-424" data-mp="25g4ve8euxagx">
              Authorised access only. The passcode is required each visit.
            </span>
          </div>
        </Card>
      </div>
    </div>;
}